import {
  app,
  Component,
  ojs,
  state,
  v,
} from "modular-openscriptjs";
import {
  empty,
  pageInfo,
  scrollToTop,
} from "../../../helpers";
import { api } from "../../../declarations";
import { dom } from "../../../fotastart";
import SearchResultProductView from "../../Views/SearchResultProductView";
import InfiniteScroll from "../../Blocks/InfiniteScroll";
import SolidIcon from "../../Blocks/SolidIcon";
import Spinner from "../../Blocks/Spinner";

const h = app("h");
const router = app("router");

ojs(SearchResultProductView, InfiniteScroll, SolidIcon, Spinner);

const PRODUCTS_HOLDER_ID = "c-browse-category-products";

export default class BrowseCategoryPage extends Component {
  subcategory = state(null);
  businessId = null;
  subcategoryId = null;

  // Plain pagination state, not reactive - the product grid is built up
  // imperatively (append-only DOM writes) rather than re-rendered from
  // an array in state, so we never hold more than one page's worth of
  // products in memory/state at a time.
  page = 1;
  lastPage = false;
  _isFetching = false;

  // Only the most recently dispatched request's response is allowed to
  // render, so a slow response for a category/subcategory we've since
  // navigated away from can never land in the wrong list.
  _fetchSeq = 0;

  render(...args) {
    this.businessId = router.params.companyId;

    // Both the category and subcategory "browse" routes render this
    // page (see ojs.routes.js), and the products endpoint filters by
    // whichever category id it is given, so either param works here.
    const nextId = router.params.subcategoryId ?? router.params.categoryId;
    if (nextId !== this.subcategoryId) {
      this.subcategoryId = nextId;
      this.resetPagination();
    }

    pageInfo("Browse Products");
    scrollToTop();

    return h.div(
      { class: "py-4 mx-auto w-100 container mb-5 py-md-5" },

      h.div(
        { class: "mb-4 d-flex align-items-center gap-3" },
        h.button(
          { class: "btn btn-white border shadow-sm rounded-circle p-2", onclick: "history.back()" },
          h.SolidIcon("arrow-left")
        ),
        v(this.subcategory, (sub) => h.h2({ class: "fw-bold mb-0" }, sub.value?.name || "Products"))
      ),

      h.InfiniteScroll({
        child: h.div({ class: "row g-3", id: PRODUCTS_HOLDER_ID }),
        callback: (entries, observer, marker) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting || this.lastPage) return;
            this.fetchProducts(marker);
          });
        },
      }),

      ...args
    );
  }

  resetPagination() {
    this.page = 1;
    this.lastPage = false;
    this.subcategory.value = null;

    // Invalidate any request still in flight at the moment of the reset
    // so a stale response can never render into the new list.
    this._fetchSeq++;
  }

  async fetchProducts(marker = null) {
    if (this.lastPage || this._isFetching || !this.subcategoryId) return;

    this._isFetching = true;
    const requestSeq = ++this._fetchSeq;
    const holder = dom.id(PRODUCTS_HOLDER_ID);

    let spinner = null;
    if (marker) {
      spinner = h.Spinner({ class: "col-12 py-4", parent: marker, resetParent: true });
    }

    try {
      const res = await api.get(
        `companies/${this.businessId}/subcategories/${this.subcategoryId}/products`,
        { page: this.page, interval: 10 },
        { silent: true },
      );

      // A newer request has since been dispatched (route/params changed
      // mid-flight) - drop this response so it can't render out of order.
      if (requestSeq !== this._fetchSeq) return;

      if (!res) return;

      const productsPage = res.data.products;
      const products = productsPage?.data ?? (Array.isArray(productsPage) ? productsPage : []);
      const currentPage = productsPage?.current_page ?? this.page;
      const lastPage = productsPage?.last_page ?? currentPage;

      if (res.data.subcategory) {
        this.subcategory.value = res.data.subcategory;
        pageInfo(`${res.data.subcategory.name} - Browse`);
      }

      if (!holder) return;

      if (currentPage === 1) {
        dom.empty(holder);
      }

      if (empty(products) && currentPage === 1) {
        h.div(
          { class: "col-12 text-center py-5 text-muted", parent: holder },
          "No products found in this subcategory.",
        );
      }

      for (const product of products) {
        if (!dom.get(`[data-browse-product-id="${product.id}"]`, holder)) {
          h.div(
            {
              class: "col-12 col-md-6",
              parent: holder,
              "data-browse-product-id": product.id,
            },
            h.SearchResultProductView(product),
          );
        }
      }

      if (!empty(products)) {
        this.page = currentPage + 1;
      }
      this.lastPage = empty(products) || currentPage >= lastPage;
    } catch (e) {
      console.error(e);
    } finally {
      spinner?.remove();
      this._isFetching = false;
    }
  }
}

ojs(BrowseCategoryPage);
